import { useEffect, useMemo, useRef, useState } from "react";
import DealerNav from "./DealerNav";
import { calculateDealerPlanning, toNonNegativeNumber } from "./dealerAppraisalPlanning";
import { dealerIdFromToken, listingComparisonText, listingLabel } from "./dealerAppraisalListing";

const API = process.env.REACT_APP_API_BASE;
const DEFAULT_TARGET_GROSS = 4000;

function formatCurrency(value) {
  if (!Number.isFinite(Number(value))) return "Unavailable";
  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function PlanningControl({ label, value, onChange, max, help }) {
  const safeValue = toNonNegativeNumber(value);
  return (
    <label className="block rounded-lg border border-slate-200 bg-white p-4">
      <span className="block text-sm font-semibold text-slate-900">{label}</span>
      {help && <span className="mt-1 block text-xs leading-5 text-slate-500">{help}</span>}
      <div className="mt-3 flex items-center gap-3">
        <input
          aria-label={`${label} slider`}
          className="min-w-0 flex-1 accent-blue-600"
          type="range"
          min="0"
          max={max}
          step="100"
          value={Math.min(safeValue, max)}
          onChange={(event) => onChange(event.target.value)}
        />
        <div className="relative w-32 shrink-0">
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-slate-500">$</span>
          <input
            aria-label={label}
            className="w-full rounded-md border border-slate-300 py-2 pl-7 pr-2 text-right text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            type="number"
            min="0"
            step="100"
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      </div>
    </label>
  );
}

export default function DealerAppraisal() {
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const currentDealerId = useMemo(() => dealerIdFromToken(token), [token]);
  const shownListingAnalytics = useRef(null);
  const [form, setForm] = useState({ vin: "", mileage: "", zip: "", accidentStatus: "no" });
  const [result, setResult] = useState(null);
  const [planning, setPlanning] = useState({ targetRetail: "", recon: "0", targetGross: String(DEFAULT_TARGET_GROSS), offer: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.gtag?.("event", "dealer_appraisal_view");
  }, []);

  const updateForm = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));
  const updatePlanning = (field, value) => setPlanning((previous) => ({ ...previous, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setResult(null);

    const payload = {
      vin: form.vin.trim(),
      mileage: Number(form.mileage),
      zip: form.zip.trim(),
      askingPrice: null,
      accidentStatus: form.accidentStatus,
    };

    if (!payload.vin || !payload.mileage || !payload.zip) {
      setError("VIN, mileage, and ZIP are required.");
      return;
    }

    window.gtag?.("event", "dealer_appraisal_submit");
    setLoading(true);
    try {
      const response = await fetch(`${API}/deal-checker/check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const details = Array.isArray(data.details) ? data.details.join(" ") : data.message || "Could not appraise this vehicle.";
        throw new Error(details);
      }

      setResult(data);
      if (data.mestimate !== null && data.mestimate !== undefined) {
        setPlanning({ targetRetail: String(Math.round(data.mestimate)), recon: "0", targetGross: String(DEFAULT_TARGET_GROSS), offer: "" });
        window.gtag?.("event", "dealer_appraisal_success");
      } else {
        window.gtag?.("event", "dealer_appraisal_no_estimate");
      }
    } catch (requestError) {
      setError(requestError.message || "Could not appraise this vehicle.");
    } finally {
      setLoading(false);
    }
  };

  const decoded = result?.decodedVehicle;
  const hasEstimate = result?.mestimate !== null && result?.mestimate !== undefined;
  const currentListing = result?.meshumVehicle?.currentListing;
  const isSameDealerListing = currentListing && String(currentListing.dealerId) === String(currentDealerId);
  const currentListingLabel = listingLabel(currentListing, currentDealerId);
  const currentListingComparison = listingComparisonText(result?.meshumVehicle?.listingAnalysis, formatCurrency);
  const targetRetail = toNonNegativeNumber(planning.targetRetail);
  const recon = toNonNegativeNumber(planning.recon);
  const targetGross = toNonNegativeNumber(planning.targetGross);
  const offer = toNonNegativeNumber(planning.offer);
  const calculations = calculateDealerPlanning({ targetRetail, recon, targetGross, offer });
  const estimateDifference = hasEstimate ? targetRetail - Number(result.mestimate) : 0;
  const estimateDifferencePercent = hasEstimate && Number(result.mestimate) > 0
    ? (estimateDifference / Number(result.mestimate)) * 100
    : 0;
  const retailSliderMax = Math.max(10000, Math.ceil(Math.max(Number(result?.mestimate || 0) * 1.25, targetRetail) / 100) * 100);
  const offerSliderMax = Math.max(10000, Math.ceil(Math.max(targetRetail, offer) / 100) * 100);

  useEffect(() => {
    if (!currentListing) return;
    const analyticsKey = `${result?.meshumVehicle?.carId}:${result?.mestimate ?? "none"}`;
    if (shownListingAnalytics.current === analyticsKey) return;
    shownListingAnalytics.current = analyticsKey;
    const analysis = result?.meshumVehicle?.listingAnalysis;
    window.gtag?.("event", "dealer_appraisal_existing_listing_shown", {
      same_dealer: Boolean(isSameDealerListing),
      listing_price: currentListing.askingPrice ?? null,
      mestimate: result?.mestimate ?? null,
      difference_amount: analysis?.differenceAmount ?? null,
      difference_percent: analysis?.differencePercent ?? null,
    });
  }, [currentListing, isSameDealerListing, result]);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-4 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <DealerNav />
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-950 sm:text-3xl">Vehicle Appraisal</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
            Estimate market value from recently sold comparable vehicles, then model a potential acquisition using your own retail, recon, gross, and offer assumptions.
          </p>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px] xl:items-start">
          <section className="space-y-6">
            <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="block lg:col-span-2">
                  <span className="text-sm font-semibold text-slate-800">VIN</span>
                  <input value={form.vin} onChange={(event) => updateForm("vin", event.target.value.toUpperCase())} maxLength={17} placeholder="17-character VIN" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-3 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-800">Mileage</span>
                  <input value={form.mileage} onChange={(event) => updateForm("mileage", event.target.value)} type="number" min="1" placeholder="Current mileage" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-3 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                </label>
                <label className="block">
                  <span className="text-sm font-semibold text-slate-800">ZIP</span>
                  <input value={form.zip} onChange={(event) => updateForm("zip", event.target.value)} maxLength={5} placeholder="ZIP code" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-3 text-slate-950 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                </label>
              </div>
              <fieldset className="mt-4">
                <legend className="text-sm font-semibold text-slate-800">Accident / damage</legend>
                <div className="mt-2 grid max-w-md grid-cols-3 gap-2">
                  {[['no', 'No'], ['yes', 'Yes'], ['unknown', 'Unknown']].map(([value, label]) => (
                    <label key={value} className={`flex cursor-pointer items-center justify-center rounded-md border px-3 py-2.5 text-sm font-semibold ${form.accidentStatus === value ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700"}`}>
                      <input className="sr-only" type="radio" name="accidentStatus" value={value} checked={form.accidentStatus === value} onChange={(event) => updateForm("accidentStatus", event.target.value)} />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              {error && <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading} className="mt-5 w-full rounded-md bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300 sm:w-auto">
                {loading ? "Calculating..." : "Appraise Vehicle"}
              </button>
            </form>

            {loading && <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-600">Calculating market value from comparable sales...</div>}

            {result && (
              <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                {decoded && <div><div className="text-sm font-semibold uppercase tracking-wide text-slate-500">Vehicle</div><h2 className="mt-1 text-2xl font-semibold text-slate-950">{[decoded.year, decoded.make, decoded.model, decoded.trim].filter(Boolean).join(" ")}</h2></div>}
                {result.meshumVehicle?.exists && <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950"><strong>This vehicle already exists in Meshum.</strong><a href={result.meshumVehicle.url} className="ml-2 font-semibold text-emerald-800 underline">View Vehicle</a></div>}
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg bg-slate-950 p-4 text-white"><div className="text-sm text-slate-300">Meshum Estimate</div><div className="mt-1 text-3xl font-semibold">{hasEstimate ? formatCurrency(result.mestimate) : "Unavailable"}</div></div>
                  <div className="rounded-lg bg-slate-50 p-4"><div className="text-sm text-slate-500">Comps used</div><div className="mt-1 text-2xl font-semibold text-slate-950">{result.compsUsedCount || 0}</div></div>
                  <div className="rounded-lg bg-slate-50 p-4"><div className="text-sm text-slate-500">Radius used</div><div className="mt-1 text-2xl font-semibold text-slate-950">{result.radiusUsed ? `${result.radiusUsed} mi` : "None"}</div></div>
                  <div className="rounded-lg bg-slate-50 p-4"><div className="text-sm text-slate-500">Confidence / status</div><div className="mt-1 text-lg font-semibold text-slate-950">{result.confidence || result.estimateStatus || "Unavailable"}</div></div>
                </div>
                {currentListing && <section className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-slate-950"><div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="text-lg font-semibold">{currentListingLabel}</h3>{currentListing.askingPrice !== null && currentListing.askingPrice !== undefined ? <div className="mt-1 text-2xl font-semibold">{formatCurrency(currentListing.askingPrice)}</div> : <p className="mt-1 text-sm text-slate-700">Current asking price is not available.</p>}</div><a href={result.meshumVehicle.url} className="font-semibold text-emerald-800 underline">View current listing <span aria-hidden="true">→</span></a></div>{!isSameDealerListing && (currentListing.dealerName || currentListing.city || currentListing.state) && <p className="mt-2 text-sm text-slate-700">{currentListing.dealerName && <>Listed by {currentListing.dealerName}</>}{currentListing.dealerName && (currentListing.city || currentListing.state) && " · "}{[currentListing.city, currentListing.state].filter(Boolean).join(", ")}</p>}{currentListingComparison && <p className="mt-2 font-semibold text-emerald-950">{currentListingComparison}{result.meshumVehicle.listingAnalysis?.pricePosition !== "Near Estimate" && result.meshumVehicle.listingAnalysis?.differencePercent !== null && result.meshumVehicle.listingAnalysis?.differencePercent !== undefined && ` · ${Math.abs(Number(result.meshumVehicle.listingAnalysis.differencePercent)).toFixed(1)}% ${Number(result.meshumVehicle.listingAnalysis.differenceAmount) < 0 ? "below" : "above"} estimate`}</p>}<p className="mt-3 text-xs leading-5 text-slate-600">This vehicle is currently listed on Meshum at the asking price shown above when available. Listing prices, availability, vehicle condition, equipment, fees, and dealer terms may change. Verify the current vehicle details before making an acquisition or pricing decision.</p></section>}
                <p className="mt-4 text-sm text-slate-600">{result.supportText || result.message || "Estimated market value based on recently sold comparable vehicles."}</p>
                {Array.isArray(result.compsUsed) && result.compsUsed.length > 0 && <div className="mt-5"><h3 className="text-lg font-semibold text-slate-950">Comparable sales used</h3><div className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200">{result.compsUsed.slice(0, 5).map((comp) => <div key={comp.id} className="grid gap-2 p-3 sm:grid-cols-[1fr_auto]"><div><div className="font-semibold text-slate-900">{[comp.year, comp.make, comp.model, comp.trim].filter(Boolean).join(" ")}</div><div className="text-sm text-slate-600">{formatNumber(comp.mileage)} miles</div></div><div className="font-semibold text-slate-950">{formatCurrency(comp.price)}</div></div>)}</div></div>}
              </section>
            )}
          </section>

          {hasEstimate && <aside className="xl:sticky xl:top-4"><section className="rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm"><h2 className="text-xl font-semibold text-slate-950">Dealer Planning</h2><p className="mt-1 text-sm leading-6 text-slate-600">Model an acquisition using your own assumptions. Changing these inputs does not rerun the valuation.</p><div className="mt-5 space-y-3"><PlanningControl label="Target Retail Price" value={planning.targetRetail} onChange={(value) => updatePlanning("targetRetail", value)} max={retailSliderMax} help="Defaults to the Meshum Estimate. Exact entry may exceed the slider range." /><PlanningControl label="Estimated Recon" value={planning.recon} onChange={(value) => updatePlanning("recon", value)} max={15000} /><PlanningControl label="Target Gross Profit" value={planning.targetGross} onChange={(value) => updatePlanning("targetGross", value)} max={20000} /><PlanningControl label="Acquisition / Offer Price" value={planning.offer} onChange={(value) => updatePlanning("offer", value)} max={offerSliderMax} help="Leave at $0 until you have an offer to model." /></div><div className="mt-5 grid gap-3 rounded-lg bg-white p-4"><Summary label="Meshum Estimate" value={formatCurrency(result.mestimate)} /><Summary label="Target Retail" value={formatCurrency(targetRetail)} detail={`${formatCurrency(Math.abs(estimateDifference))} ${estimateDifference < 0 ? "below" : estimateDifference > 0 ? "above" : "at"} Meshum Estimate · ${Math.abs(estimateDifferencePercent).toFixed(1)}%`} /><Summary label="Maximum Acquisition Based on Your Inputs" value={formatCurrency(calculations.maximumAcquisition)} /><Summary label="Your Offer" value={planning.offer === "" ? "Not entered" : formatCurrency(offer)} /><Summary label="Potential Gross" value={planning.offer === "" ? "Enter an offer" : formatCurrency(calculations.potentialGross)} /><Summary label="Potential Gross Margin" value={planning.offer === "" ? "Enter an offer" : `${calculations.potentialGrossMarginPercent.toFixed(1)}%`} /></div><p className="mt-5 rounded-md border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-600">Meshum Estimates and dealer-planning calculations are provided for informational purposes only. They are not appraisals, purchase offers, guarantees of vehicle value, resale price, or profit. Actual values, reconditioning costs, selling prices, and financial results may vary based on vehicle condition, history, market conditions, location, fees, inventory, and other factors.</p></section></aside>}
        </div>
      </div>
    </main>
  );
}

function Summary({ label, value, detail }) {
  return <div className="flex items-start justify-between gap-3"><div><div className="text-sm text-slate-500">{label}</div>{detail && <div className="mt-0.5 text-xs text-slate-500">{detail}</div>}</div><div className="text-right text-lg font-semibold text-slate-950">{value}</div></div>;
}
